import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { EnvironmentProvider } from './contexts/EnvironmentContext'
import { TeamPortalAuthProvider, useTeamPortalAuth } from './contexts/TeamPortalAuthContext'
import Login from './pages/Login'
import JoinInvite from './pages/JoinInvite'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import UserDetail from './pages/UserDetail'
import Teams from './pages/Teams'
import TeamDetail from './pages/TeamDetail'
import Payments from './pages/Payments'
import Communications from './pages/Communications'
import Support from './pages/Support'
import Settings from './pages/Settings'
import Analytics from './pages/Analytics'
import Invoices from './pages/Invoices'
import Subscriptions from './pages/Subscriptions'
import Operations from './pages/Operations'
import Layout from './components/Layout'
import TeamPortalLayout from './components/TeamPortalLayout'
import PortalLogin from './pages/portal/PortalLogin'
import PortalSignup from './pages/portal/PortalSignup'
import PortalForgotPassword from './pages/portal/PortalForgotPassword'
import PortalResetPassword from './pages/portal/PortalResetPassword'
import PortalDashboard from './pages/portal/PortalDashboard'
import PortalSchedule from './pages/portal/PortalSchedule'
import PortalRoster from './pages/portal/PortalRoster'
import PortalMessages from './pages/portal/PortalMessages'
import PortalProfile from './pages/portal/PortalProfile'
import PortalInvoices from './pages/portal/PortalInvoices'
import PortalEventDetail from './pages/portal/PortalEventDetail'
import PortalEventForm from './pages/portal/PortalEventForm'
import PortalNotifications from './pages/portal/PortalNotifications'
import PortalPlayerDetail from './pages/portal/PortalPlayerDetail'
import PortalAddPlayer from './pages/portal/PortalAddPlayer'
import PortalChat from './pages/portal/PortalChat'
import PortalCalendar from './pages/portal/PortalCalendar'
import PortalCreateTeam from './pages/portal/PortalCreateTeam'
import JoinTeamWithCode from './pages/portal/JoinTeamWithCode'
import MyJoinRequests from './pages/portal/MyJoinRequests'
import TeamJoinRequests from './pages/portal/TeamJoinRequests'
import TeamInviteCodes from './pages/portal/TeamInviteCodes'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/login" />
  }
  
  return children
}

function PortalProtectedRoute({ children }) {
  const { user, loading } = useTeamPortalAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }
  
  if (!user) {
    return <Navigate to="/portal/login" />
  }
  
  return children
}

export default function App() {
  return (
    <AuthProvider>
      <EnvironmentProvider>
        <TeamPortalAuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/join/:token" element={<JoinInvite />} />
            
            {/* Team Portal Routes — Public */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/signup" element={<PortalSignup />} />
            <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
            <Route path="/portal/reset-password" element={<PortalResetPassword />} />
            <Route path="/reset-password" element={<Navigate to={`/portal/reset-password${window.location.search}`} replace />} />
            
            {/* Team Portal Routes — Protected */}
            <Route path="/portal/join" element={
              <PortalProtectedRoute>
                <JoinTeamWithCode />
              </PortalProtectedRoute>
            } />
            <Route path="/portal/my-requests" element={
              <PortalProtectedRoute>
                <MyJoinRequests />
              </PortalProtectedRoute>
            } />
            <Route path="/portal/create-team" element={
              <PortalProtectedRoute>
                <PortalCreateTeam />
              </PortalProtectedRoute>
            } />
            <Route path="/portal" element={
              <PortalProtectedRoute>
                <TeamPortalLayout />
              </PortalProtectedRoute>
            }>
              <Route index element={<PortalDashboard />} />
              <Route path="team/:teamId" element={<PortalDashboard />} />
              <Route path="team/:teamId/schedule" element={<PortalSchedule />} />
              <Route path="team/:teamId/calendar" element={<PortalCalendar />} />
              <Route path="team/:teamId/roster" element={<PortalRoster />} />
              <Route path="team/:teamId/messages" element={<PortalMessages />} />
              <Route path="team/:teamId/chat" element={<PortalChat />} />
              <Route path="team/:teamId/invoices" element={<PortalInvoices />} />
              <Route path="team/:teamId/notifications" element={<PortalNotifications />} />
              <Route path="team/:teamId/profile" element={<PortalProfile />} />
              <Route path="team/:teamId/events/new" element={<PortalEventForm />} />
              <Route path="team/:teamId/events/:eventId" element={<PortalEventDetail />} />
              <Route path="team/:teamId/events/:eventId/edit" element={<PortalEventForm />} />
              <Route path="team/:teamId/players/new" element={<PortalAddPlayer />} />
              <Route path="team/:teamId/players/:playerId" element={<PortalPlayerDetail />} />
              <Route path="team/:teamId/join-requests" element={<TeamJoinRequests />} />
              <Route path="team/:teamId/invite-codes" element={<TeamInviteCodes />} />
            </Route>
            
            {/* Admin Portal Routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="users/:userId" element={<UserDetail />} />
              <Route path="teams" element={<Teams />} />
              <Route path="teams/:teamId" element={<TeamDetail />} />
              <Route path="payments" element={<Payments />} />
              {/* Legacy routes - redirect to Payments */}
              <Route path="revenue" element={<Navigate to="/payments" replace />} />
              <Route path="stripe" element={<Navigate to="/payments" replace />} />
              <Route path="promo-codes" element={<Navigate to="/payments" replace />} />
              <Route path="communications" element={<Communications />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              {/* Legacy routes - redirect to Communications */}
              <Route path="alerts" element={<Navigate to="/communications" replace />} />
              <Route path="posts" element={<Navigate to="/communications" replace />} />
              <Route path="support" element={<Support />} />
              <Route path="operations" element={<Operations />} />
              {/* Settings with sub-routes */}
              <Route path="settings/*" element={<Settings />} />
              {/* Legacy routes - redirect to Settings */}
              <Route path="moderation" element={<Navigate to="/settings/moderation" replace />} />
              <Route path="developers" element={<Navigate to="/settings/developers" replace />} />
              <Route path="teams/map" element={<Navigate to="/teams" replace />} />
              <Route path="events" element={<Navigate to="/analytics" replace />} />
            </Route>
          </Routes>
        </TeamPortalAuthProvider>
      </EnvironmentProvider>
    </AuthProvider>
  )
}
